/** @jsxImportSource @emotion/react */
import type { ReactNode } from 'react';

import { CloseIcon } from '@kaiizen/kai-shiki/react';
import { DEFAULT_SIDE_DRAWER_STYLES, type SideDrawerStyles } from './sideDrawerStyles';

interface SideDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Names the drawer for assistive tech - it renders as a labelled `aside`. */
  ariaLabel: string;
  closeLabel: string;
  /** Title, breadcrumbs, counts: whatever sits beside the close button. */
  header: ReactNode;
  /** The scrolling content. */
  children: ReactNode;
  /** Pinned below the scrolling content - e.g. a composer. Omitted entirely when not supplied. */
  footer?: ReactNode;
  testId?: string;
  bodyTestId?: string;
  styles?: SideDrawerStyles;
}

/**
 * The right-side drawer for any panel content. Not a modal: it sits over the right of the nearest
 * positioned ancestor without a scrim, so whatever is beside it stays usable while it's open, and
 * it stays open until its close button is pressed. The caller owns the open state and the content.
 */
export const SideDrawer = ({
  open,
  onClose,
  ariaLabel,
  closeLabel,
  header,
  children,
  footer,
  testId,
  bodyTestId,
  styles = DEFAULT_SIDE_DRAWER_STYLES,
}: SideDrawerProps) => {
  if (!open) return null;
  return (
    <aside css={styles.root} aria-label={ariaLabel} data-testid={testId}>
      <div css={styles.header}>
        <div css={styles.headerContent}>{header}</div>
        <button type={'button'} css={styles.closeButton} aria-label={closeLabel} onClick={onClose}>
          <CloseIcon size={14} aria-hidden={'true'} />
        </button>
      </div>
      <div css={styles.body} data-testid={bodyTestId}>{children}</div>
      {footer ? <div css={styles.footer}>{footer}</div> : null}
    </aside>
  );
};
